import supabase from '../config/supabase';

// ✅ 移除所有生產環境 console.log，只保留 console.error
class PetService {
  constructor() {
    this.supabase = supabase;
    this.cachedUser = null;
    this.userCacheTime = 0;
    this.USER_CACHE_DURATION = 60000; // 1分鐘

    this.cachedPets = null;
    this.petsCacheTime = 0;
    this.PETS_CACHE_DURATION = 300000; // 5分鐘
  }

  async getCachedUser() {
    const now = Date.now();
    if (this.cachedUser && (now - this.userCacheTime) < this.USER_CACHE_DURATION) {
      return { user: this.cachedUser, error: null };
    }
    const { data: { user }, error } = await this.supabase.auth.getUser();
    if (!error && user) {
      this.cachedUser = user;
      this.userCacheTime = now;
    }
    return { user, error };
  }

  async getPets(forceRefresh = false) {
    try {
      const now = Date.now();
      if (!forceRefresh && this.cachedPets && (now - this.petsCacheTime) < this.PETS_CACHE_DURATION) {
        return { data: this.cachedPets, error: null, fromCache: true };
      }

      const { user, error: userError } = await this.getCachedUser();
      if (userError || !user) throw new Error('無法獲取使用者資訊，請重新登入。');

      const { data, error } = await this.supabase
        .from('pets')
        .select(`
          id, name, type, breed, gender, birth_date, age, photo_url,
          feeding_plan, feeding_schedule, medication_plan, medication_schedule,
          current_location, next_vaccine_date, is_shared, share_token,
          share_permissions, created_at
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw new Error(`獲取寵物資料失敗: ${error.message}`);

      this.cachedPets = data || [];
      this.petsCacheTime = now;
      return { data: this.cachedPets, error: null, fromCache: false };
    } catch (error) {
      console.error('PetService getPets error:', error);
      return { data: null, error };
    }
  }

  clearCache() {
    this.cachedPets = null;
    this.petsCacheTime = 0;
  }

  updateCachedPet(updatedPet) {
    if (!this.cachedPets) return;
    const index = this.cachedPets.findIndex(p => p.id === updatedPet.id);
    if (index !== -1) this.cachedPets[index] = updatedPet;
  }

  removeCachedPet(petId) {
    if (!this.cachedPets) return;
    this.cachedPets = this.cachedPets.filter(p => p.id !== petId);
  }

  async getPetById(petId) {
    try {
      if (this.cachedPets) {
        const cachedPet = this.cachedPets.find(p => p.id === petId);
        if (cachedPet) return cachedPet;
      }

      const { user, error: userError } = await this.getCachedUser();
      if (userError || !user) throw new Error('無法獲取使用者資訊，請重新登入。');

      const { data, error } = await this.supabase
        .from('pets')
        .select('*')
        .eq('id', petId)
        .eq('user_id', user.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') throw new Error('找不到指定的寵物資料');
        throw new Error(`獲取寵物資料失敗: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('PetService getPetById error:', error);
      throw error;
    }
  }

  async getPetByShareToken(shareToken) {
    try {
      if (!shareToken) throw new Error('分享連結無效：缺少分享標識');

      const decodedToken = decodeURIComponent(shareToken);
      const { data, error } = await this.supabase
        .from('pets')
        .select('*')
        .eq('share_token', decodedToken)
        .eq('is_shared', true)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null;
        throw new Error(`查詢分享寵物失敗: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('PetService getPetByShareToken error:', error);
      throw error;
    }
  }

  async savePet(petData) {
    try {
      const { user, error: userError } = await this.getCachedUser();
      if (userError || !user) throw new Error('無法獲取使用者資訊，請重新登入。');

      const { id, created_at, updated_at, ...cleanData } = petData;

      const dataToSave = {
        ...cleanData,
        user_id: user.id,
        is_shared: cleanData.is_shared !== undefined ? cleanData.is_shared : false,
      };

      Object.keys(dataToSave).forEach(key => {
        if (dataToSave[key] === '') dataToSave[key] = null;
      });

      let result;
      if (petData.id) {
        const { data, error } = await this.supabase
          .from('pets')
          .update(dataToSave)
          .eq('id', petData.id)
          .eq('user_id', user.id)
          .select()
          .single();
        if (error) throw new Error(`更新寵物資料失敗: ${error.message}`);
        result = data;
        this.updateCachedPet(result);
      } else {
        const { data, error } = await this.supabase
          .from('pets')
          .insert([dataToSave])
          .select()
          .single();
        if (error) throw new Error(`新增寵物資料失敗: ${error.message}`);
        result = data;
        if (this.cachedPets) this.cachedPets.unshift(result);
      }

      return result;
    } catch (error) {
      console.error('PetService savePet error:', error);
      throw error;
    }
  }

  async togglePetSharing(petId, isShared) {
    try {
      const { user, error: userError } = await this.getCachedUser();
      if (userError || !user) throw new Error('無法獲取使用者資訊，請重新登入。');

      const { data, error } = await this.supabase
        .from('pets')
        .update({ is_shared: isShared })
        .eq('id', petId)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw new Error(`切換分享狀態失敗: ${error.message}`);
      this.updateCachedPet(data);
      return data;
    } catch (error) {
      console.error('PetService togglePetSharing error:', error);
      throw error;
    }
  }

  async updateSharePermissions(petId, permissions) {
    try {
      const { user, error: userError } = await this.getCachedUser();
      if (userError || !user) throw new Error('無法獲取使用者資訊，請重新登入。');

      const { data, error } = await this.supabase
        .from('pets')
        .update({ share_permissions: permissions })
        .eq('id', petId)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw new Error(`更新分享權限失敗: ${error.message}`);
      this.updateCachedPet(data);
      return data;
    } catch (error) {
      console.error('PetService updateSharePermissions error:', error);
      throw error;
    }
  }

  async deletePet(petId) {
    try {
      const { user, error: userError } = await this.getCachedUser();
      if (userError || !user) throw new Error('無法獲取使用者資訊，請重新登入。');

      const { error } = await this.supabase
        .from('pets')
        .delete()
        .eq('id', petId)
        .eq('user_id', user.id);

      if (error) throw new Error(`刪除寵物失敗: ${error.message}`);
      this.removeCachedPet(petId);
      return { success: true, error: null };
    } catch (error) {
      console.error('PetService deletePet error:', error);
      return { success: false, error };
    }
  }

  async checkConnection() {
    try {
      const { data: { session }, error: sessionError } = await this.supabase.auth.getSession();
      if (sessionError) throw new Error(`身份驗證階段出錯: ${sessionError.message}`);
      if (!session) throw new Error('使用者未登入，無法檢查資料庫連線。');
      const { error } = await this.supabase.from('pets').select('*').limit(0);
      if (error) {
        if (error.code === '42P01') throw new Error("資料庫 'pets' 表格不存在。請執行遷移腳本。");
        throw new Error(`無法訪問 'pets' 表: ${error.message}`);
      }
      return { connected: true, error: null };
    } catch (error) {
      console.error('資料庫連線檢查失敗:', error);
      return { connected: false, error: error.message };
    }
  }

  async checkAuthentication() {
    try {
      const { user, error } = await this.getCachedUser();
      if (error) throw error;
      return { authenticated: !!user, user };
    } catch (error) {
      console.error('Authentication check failed:', error);
      return { authenticated: false, user: null };
    }
  }
}

export default PetService;